import React, { useState, useCallback, ReactNode } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OnChangeCallback } from '../../util/UtilityTypes'

interface OrderableListProps<T> {
    name: string
    parts: T[]
    disabled?: boolean
    onChange: OnChangeCallback<T[]>
    renderItem: (item: T) => ReactNode
}

const hasParent = (element: Element, clazz: string): boolean => {
    if (element.classList.contains(clazz)) {
        return true;
    } else if (element.parentNode === undefined) {
        return false;
    } else if (!(element.parentNode instanceof Element)) {
        return false;
    }

    return hasParent(element.parentNode, clazz);
}

const OrderableList = <T,>({ parts, name, onChange, renderItem }: OrderableListProps<T>) => {

    const [dragging, setDragging] = useState<{ from: number, to: number, width: number } | undefined>(undefined);

    const [partsKey, setPartsKey] = useState<number>(Math.random());

    const removePart = useCallback((idx: number) => {
        const partsCopy = [...parts];
        partsCopy.splice(idx, 1);
        onChange(name, partsCopy);
    }, [parts, name, onChange])

    const onStart: DraggableEventHandler = useCallback((event, data) => {

        const idx = data.node.getAttribute("data-idx");

        if (idx === null) return;

        if (event.target instanceof Element && hasParent(event.target, 'orderable-list__remove')) {
            removePart(parseInt(idx, 10));
            return;
        }

        setDragging({
            from: parseInt(idx, 10),
            to: parseInt(idx, 10),
            width: data.node.getBoundingClientRect().width + parseInt(getComputedStyle(data.node).marginLeft, 10)
        });
    }, [removePart])

    const onDrag: DraggableEventHandler = useCallback((event, data) => {
        if (dragging === undefined) {
            return;
        }

        let currentX = data.node.getBoundingClientRect().x;
        let targetNode: Element | undefined = data.node;
        if (data.x > 0) {
            let curr = data.node.nextElementSibling;
            while (curr && curr.getBoundingClientRect().x < currentX) {
                targetNode = curr;
                curr = curr.nextElementSibling;
            }
        }
        else {
            let curr = data.node.previousElementSibling;
            while (curr && curr.getBoundingClientRect().x > currentX) {
                targetNode = curr;
                curr = curr.previousElementSibling;
            }
        }

        if (targetNode === undefined) {
            return;
        }

        const targetIdx = targetNode.getAttribute("data-idx");

        if (targetIdx === null) {
            return;
        }

        const newTo = parseInt(targetIdx, 10);

        if (newTo !== dragging.to) {
            setDragging({ ...dragging, to: newTo })
        }
    }, [dragging])

    const onStop: DraggableEventHandler = useCallback((event, data) => {
        if (dragging === undefined) {
            return;
        }

        const partsCopy = [...parts];
        partsCopy.splice(dragging.to, 0, partsCopy.splice(dragging.from, 1)[0]);
        setDragging(undefined);
        onChange(name, partsCopy);

        setPartsKey(Math.random());
    }, [dragging, parts, name, onChange]);

    return (
        <div className="orderable-list">
            {parts.map((x, i) => {
                let key = i.toString();

                let xOffset = 0;
                if (dragging !== undefined) {
                    if (dragging.from > dragging.to) {
                        if (i >= dragging.to && i < dragging.from) {
                            xOffset = dragging.width;
                        }
                    }
                    else if (dragging.from < dragging.to) {
                        if (i > dragging.from && i <= dragging.to) {
                            xOffset = -dragging.width;
                        }
                    }
                }

                return (
                    <Draggable onStart={onStart} onDrag={onDrag} onStop={onStop} key={partsKey + key} axis="x" positionOffset={{ x: xOffset, y: 0 }}>
                        <div className="orderable-list__item" data-idx={key}>
                            {renderItem(x)}
                            <FontAwesomeIcon icon="trash" className="orderable-list__remove" style={{ marginLeft: '1rem' }} />
                        </div>
                    </Draggable>
                )
            })}
        </div>
    );
}

export default OrderableList;